import { DialogComponent } from '@theme/dialog';

const SEEN_KEY = 'welcome-popup:seen';
const PENDING_KEY = 'welcome-popup:pending';
const SUBSCRIBED_KEY = 'welcome-popup:subscribed';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Remembers editor selection across preview reloads, which can fire `select` before this module runs. */
const EDITOR_SELECTED_KEY = 'welcome-popup:editor-selected';

/**
 * Shows the welcome newsletter popup on a first visit, then again only after the
 * merchant-set number of days, and never after the shopper subscribes in this browser.
 * Reopens after a popup submission reloads the page so the shopper sees the result.
 *
 * @extends DialogComponent
 */
class WelcomePopupComponent extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit, { signal });

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal });
      if (readStorage(sessionStorage, EDITOR_SELECTED_KEY)) this.showDialog();
      return;
    }

    const posted = new URL(window.location.href).searchParams.has('customer_posted');
    const hasError = Boolean(this.querySelector('[aria-invalid]'));
    if (posted && !hasError) writeStorage(localStorage, SUBSCRIBED_KEY, '1');

    if (readStorage(sessionStorage, PENDING_KEY)) {
      removeStorage(sessionStorage, PENDING_KEY);
      if (posted || hasError) {
        this.showDialog();
        return;
      }
    }

    if (this.hasAttribute('subscribed') || readStorage(localStorage, SUBSCRIBED_KEY)) return;

    const lastSeen = Number(readStorage(localStorage, SEEN_KEY));
    if (lastSeen && Date.now() - lastSeen < this.repeatDays * DAY_MS) return;

    this.#timeout = window.setTimeout(() => {
      writeStorage(localStorage, SEEN_KEY, String(Date.now()));
      this.showDialog();
    }, this.delay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    window.clearTimeout(this.#timeout);
  }

  /**
   * Delay in seconds before the popup opens on a first visit.
   * @returns {number}
   */
  get delay() {
    return Number(this.getAttribute('delay')) || 0;
  }

  /**
   * Days to wait before showing the popup again to a shopper who closed it.
   * @returns {number}
   */
  get repeatDays() {
    return Number(this.getAttribute('repeat-days')) || 30;
  }

  #handleSubmit = () => {
    writeStorage(sessionStorage, PENDING_KEY, '1');
  };

  /**
   * Editor events target the section wrapper and may be created in another realm,
   * so match on the target rather than `instanceof CustomEvent`.
   * @param {Event} event
   */
  #isOwnSectionEvent(event) {
    return event.target instanceof Node && event.target.contains(this);
  }

  /** @param {Event} event */
  #handleEditorSelect = (event) => {
    if (!this.#isOwnSectionEvent(event)) return;
    writeStorage(sessionStorage, EDITOR_SELECTED_KEY, '1');
    this.showDialog();
  };

  /** @param {Event} event */
  #handleEditorDeselect = (event) => {
    if (!this.#isOwnSectionEvent(event)) return;
    removeStorage(sessionStorage, EDITOR_SELECTED_KEY);
    this.closeDialog();
  };
}

/**
 * Storage can throw in private browsing or when cookies are blocked; treat that as empty.
 * @param {Storage} storage
 * @param {string} key
 */
function readStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 */
function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore unavailable storage; the popup may show again next visit.
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 */
function removeStorage(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore unavailable storage.
  }
}

if (!customElements.get('welcome-popup-component')) {
  customElements.define('welcome-popup-component', WelcomePopupComponent);
}
